import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Title3,
  Spinner,
  Text,
  Caption1,
  Button,
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  DialogContent,
  Badge,
  Combobox,
  Input,
  Option,
  TagGroup,
  Tag,
} from "@fluentui/react-components";
import {
  ArrowUpload20Regular,
  ArrowDownload20Regular,
  Eye20Regular,
  Link20Regular,
  Delete20Regular,
  Dismiss24Regular,
  Document20Regular,
  ArrowSync20Regular,
  Add16Regular,
  Search20Regular,
} from "@fluentui/react-icons";
import { useSearchParams } from "react-router-dom";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import {
  ClientDocumentDto,
  ClientDocumentFileDto,
  DocumentTag,
  getClientDocuments,
  createClientDocument,
  deleteClientDocument,
  downloadClientDocumentVersion,
  updateClientDocumentTags,
  getClientDocumentVersions,
  uploadClientDocumentVersion,
} from "../../services/client-documents.service";
import { getUserFriendlyErrorMessage } from "../../utils/error-messages";
import { useTypographyStyles } from "../../styles/typography.styles";
import { useTrackEvent } from "../../hooks/useTrackEvent";
import { environment } from "../../config/environment";
import { useCloseOnNavigation } from "../../hooks/useCloseOnNavigation";

/**
 * Upload size ceiling quoted to users in the 413 message.
 *
 * Must stay in step with FOUR server-side limits, all of which have to
 * agree or the smallest silently wins:
 *   - ClientDocumentUploadLimits.MaxRequestBytes (the [RequestSizeLimit]
 *     attribute on the controller)
 *   - FormOptions.MultipartBodyLengthLimit     } all three set from that
 *   - Kestrel's MaxRequestBodySize             } same constant in
 *   - IISServerOptions.MaxRequestBodySize      } Program.cs
 *   - IIS request filtering's maxAllowedContentLength, in the API's
 *     web.config — a separate cap enforced before the request reaches
 *     ASP.NET Core at all
 *
 * The server cap is exactly 100 MiB (104,857,600 bytes) rather than
 * 100,000,000, chosen so this number is honest: Windows Explorer labels
 * MiB as "MB", so a file the user sees as "100 MB" is right at the
 * limit rather than 5% over it.
 *
 * Quoted here because the limit is enforced before any controller runs,
 * so there is no server-side message to surface — this is the only place
 * the user can learn what the cap is.
 */
const MAX_UPLOAD_MB = 100;
const FILE_TOO_LARGE_MESSAGE =
  `That file is too large to upload. The limit is ${MAX_UPLOAD_MB}MB per file.`;

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    height: "100%",
    // Without border-box, padding is added on top of the 100% height and the
    // page overflows by the padding amount even when empty — phantom scrollbar.
    boxSizing: "border-box",
    overflow: "auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerActions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXXXL,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    textAlign: "center",
    color: tokens.colorPaletteRedForeground1,
  },
  tableContainer: {
    overflowX: "auto",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  thead: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  th: {
    padding: tokens.spacingVerticalM,
    textAlign: "left",
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  tbody: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  tr: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    transition: "background-color 0.2s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  td: {
    padding: tokens.spacingVerticalM,
    verticalAlign: "middle",
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  nameIcon: {
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
  },
  tagList: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    flexWrap: "wrap",
    cursor: "pointer",
    alignItems: "center",
  },
  tagBadge: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "120px",
  },
  versionLink: {
    cursor: "pointer",
    color: tokens.colorBrandForeground1,
    ":hover": {
      textDecorationLine: "underline",
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXXL,
    gap: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
  },
  noClientState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXXL,
    gap: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
  },
  uploadSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  filePickerBox: {
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    border: `2px dashed ${tokens.colorNeutralStroke1}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalS,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  filePickerBoxDragging: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    backgroundColor: tokens.colorBrandBackground2,
  },
  selectedFileInfo: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  hiddenInput: {
    display: "none",
  },
  dialogSurface: {
    maxWidth: "500px",
    minWidth: "400px",
  },
  wideDialogSurface: {
    maxWidth: "700px",
    minWidth: "500px",
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    textAlign: "center",
  },
  tagInputSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  selectedTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
  },
  actionsCell: {
    display: "flex",
    gap: "2px",
  },
});

// Reusable key-value tag input component (Azure resource tags style)
const TagInput: React.FC<{
  selectedTags: DocumentTag[];
  onTagsChange: (tags: DocumentTag[]) => void;
  allKnownKeys: string[];
  keyInput: string;
  onKeyInputChange: (value: string) => void;
  valueInput: string;
  onValueInputChange: (value: string) => void;
  disabled?: boolean;
}> = ({ selectedTags, onTagsChange, allKnownKeys, keyInput, onKeyInputChange, valueInput, onValueInputChange, disabled }) => {
  const styles = useStyles();

  const keySuggestions = useMemo(() => {
    const lower = keyInput.toLowerCase();
    const usedKeys = new Set(selectedTags.map((t) => t.key.toLowerCase()));
    return allKnownKeys
      .filter((k) => !usedKeys.has(k.toLowerCase()))
      .filter((k) => !lower || k.toLowerCase().includes(lower));
  }, [keyInput, allKnownKeys, selectedTags]);

  const addTag = () => {
    const key = keyInput.trim();
    if (!key) return;
    // Replace if key already exists (same as Azure behaviour)
    const filtered = selectedTags.filter((t) => t.key.toLowerCase() !== key.toLowerCase());
    onTagsChange([...filtered, { key, value: valueInput.trim() }]);
    onKeyInputChange("");
    onValueInputChange("");
  };

  const tagId = (t: DocumentTag) => `${t.key}::${t.value}`;

  return (
    <div className={styles.tagInputSection}>
      <div style={{ display: "flex", gap: "4px", alignItems: "flex-end" }}>
        <div style={{ flex: "0 1 40%" }}>
          <Caption1>Name</Caption1>
          <Combobox
            value={keyInput}
            onChange={(e) => onKeyInputChange((e.target as HTMLInputElement).value)}
            onOptionSelect={(_e, data) => {
              if (data.optionValue) onKeyInputChange(data.optionValue);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && keyInput.trim()) {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="Tag name..."
            freeform
            disabled={disabled}
            listbox={{ style: { maxHeight: "200px" } }}
            size="small"
            autoComplete="off"
          >
            {keySuggestions.map((k) => (
              <Option key={k} value={k} text={k}>{k}</Option>
            ))}
            {keyInput.trim() && !allKnownKeys.some((k) => k.toLowerCase() === keyInput.trim().toLowerCase()) && (
              <Option key="__new__" value={keyInput.trim()} text={keyInput.trim()}>
                New: "{keyInput.trim()}"
              </Option>
            )}
          </Combobox>
        </div>
        <div style={{ flex: 1 }}>
          <Caption1>Value</Caption1>
          <Input
            value={valueInput}
            onChange={(_e, data) => onValueInputChange(data.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && keyInput.trim()) {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="Tag value..."
            disabled={disabled}
            size="small"
          />
        </div>
        <Button
          size="small"
          appearance="subtle"
          icon={<Add16Regular />}
          disabled={disabled || !keyInput.trim()}
          onClick={addTag}
        />
      </div>
      {selectedTags.length > 0 && (
        <TagGroup
          className={styles.selectedTags}
          onDismiss={(_e, data) => {
            onTagsChange(selectedTags.filter((t) => tagId(t) !== data.value));
          }}
        >
          {selectedTags.map((tag) => (
            <Tag
              key={tagId(tag)}
              value={tagId(tag)}
              dismissible
              size="small"
              appearance="outline"
            >
              <span style={{ fontWeight: 600 }}>{tag.key}</span>
              {tag.value ? `: ${tag.value}` : ""}
            </Tag>
          ))}
        </TagGroup>
      )}
    </div>
  );
};

/**
 * Wires drag-and-drop file picking to a target element. Returns an `isDragging`
 * flag (for highlighting) and a set of handlers to spread onto the drop target.
 * The same `onFile` callback is also called when the user picks via the file
 * input, so validation lives in one place.
 */
/**
 * Drag-drop hook. Supports both single-file callbacks (legacy — one call per
 * drop) and multi-file callbacks (one call with every dropped file). Which
 * variant fires depends on the callback shape at the call site.
 */
function useFileDrop(onFiles: (files: File[]) => void, disabled?: boolean) {
  const [isDragging, setIsDragging] = useState(false);
  const handlers = {
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) setIsDragging(true);
    },
    onDragLeave: (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length > 0) onFiles(files);
    },
  };
  return { isDragging, handlers };
}

/**
 * Client document library — list, upload, version history, tags, download.
 */
const Documents: React.FC = () => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const { workbookSetup, isStandalone } = useWorkflowIntelligence();
  const clientId = workbookSetup?.clientId;
  const track = useTrackEvent();

  const [documents, setDocuments] = useState<ClientDocumentDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search bar — initialised from the `?search=` query param so other pages
  // can deep-link a pre-filled search. Edits round-trip back to the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("search") ?? "");

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    const next = new URLSearchParams(searchParams);
    if (value) next.set("search", value);
    else next.delete("search");
    setSearchParams(next, { replace: true });
  };

  const filteredDocuments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((doc) => {
      const fileName = doc.latestVersion?.fileName?.toLowerCase() ?? "";
      if (fileName.includes(q)) return true;
      return doc.tags.some(
        (t) => t.key.toLowerCase().includes(q) || (t.value?.toLowerCase() ?? "").includes(q)
      );
    });
  }, [documents, searchQuery]);

  // All known tag keys across all documents (for autocomplete suggestions)
  const allKnownKeys = useMemo(() => {
    const keySet = new Set<string>();
    documents.forEach((d) => d.tags.forEach((t) => keySet.add(t.key)));
    return Array.from(keySet).sort();
  }, [documents]);

  // Upload dialog state — the dialog processes a batch of files sequentially,
  // reusing the same tag entry UI for each. `uploadQueue` is the full batch,
  // `uploadIndex` points at the current file. Tags are RETAINED between files
  // in the batch (users often upload N similar docs — bank statements, receipts —
  // and want the same tags on all of them; they can still tweak per file).
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<File[]>([]);
  const [uploadIndex, setUploadIndex] = useState(0);
  const [uploadTags, setUploadTags] = useState<DocumentTag[]>([]);
  const [uploadTagKeyInput, setUploadTagKeyInput] = useState("");
  const [uploadTagValueInput, setUploadTagValueInput] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit tags dialog state
  const [editTagsDialogOpen, setEditTagsDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<ClientDocumentDto | null>(null);
  const [editTags, setEditTags] = useState<DocumentTag[]>([]);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [editTagKeyInput, setEditTagKeyInput] = useState("");
  const [editTagValueInput, setEditTagValueInput] = useState("");

  // Version history dialog state
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [versionDoc, setVersionDoc] = useState<ClientDocumentDto | null>(null);
  const [versions, setVersions] = useState<ClientDocumentFileDto[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);

  // Upload new version dialog state
  const [uploadVersionDialogOpen, setUploadVersionDialogOpen] = useState(false);
  const [uploadVersionDoc, setUploadVersionDoc] = useState<ClientDocumentDto | null>(null);
  const [uploadVersionFile, setUploadVersionFile] = useState<File | null>(null);
  const [isUploadingVersion, setIsUploadingVersion] = useState(false);
  const [uploadVersionError, setUploadVersionError] = useState<string | null>(null);
  const versionFileInputRef = useRef<HTMLInputElement>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<ClientDocumentDto | null>(null);

  // Download tracking
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useCloseOnNavigation(() => {
    setUploadDialogOpen(false);
    setEditTagsDialogOpen(false);
    setVersionDialogOpen(false);
    setUploadVersionDialogOpen(false);
    setDeleteDialogOpen(false);
  });

  const loadDocuments = useCallback(async () => {
    if (!clientId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getClientDocuments(clientId);
      setDocuments(data);
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-NZ", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Upload handlers
  const currentUploadFile: File | null =
    uploadIndex < uploadQueue.length ? uploadQueue[uploadIndex] : null;

  const openUploadDialog = () => {
    setUploadQueue([]);
    setUploadIndex(0);
    setUploadTags([]);
    setUploadTagKeyInput("");
    setUploadTagValueInput("");
    setUploadError(null);
    setUploadDialogOpen(true);
  };

  const closeUploadDialog = () => {
    setUploadDialogOpen(false);
    // Clear the file input's value so re-picking the same file in a fresh
    // batch still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const acceptUploadFiles = (files: File[]) => {
    // Validate every file up front so a bad one at the end doesn't waste the
    // uploads that would have preceded it. If any fails, we reject the whole
    // set — clearer than partial-accept.
    const tooBig = files.find((f) => f.size > 100 * 1024 * 1024);
    if (tooBig) {
      setUploadError(`"${tooBig.name}" is larger than 100MB. Please upload files under 100MB.`);
      return;
    }
    // Appending (rather than replacing) means a user can drop, drop again to
    // grow the batch before starting to upload.
    setUploadQueue((prev) => [...prev, ...files]);
    setUploadError(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) acceptUploadFiles(files);
    // Clear so the same file can be picked again if the user cancels and retries.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const { isDragging: isUploadDragging, handlers: uploadDropHandlers } =
    useFileDrop(acceptUploadFiles, isUploading);

  const removeQueuedFile = (index: number) => {
    setUploadQueue((prev) => prev.filter((_, i) => i !== index));
    // If the removed file was already before the cursor, shift the cursor
    // back so we keep pointing at the same "current" file.
    setUploadIndex((prev) => (index < prev ? Math.max(0, prev - 1) : prev));
  };

  const handleUploadSubmit = async () => {
    if (!clientId || !currentUploadFile) return;
    // Commit any in-progress tag before uploading, same rule as before.
    let tagsToUpload = uploadTags;
    const pendingKey = uploadTagKeyInput.trim();
    if (pendingKey) {
      const filtered = tagsToUpload.filter((t) => t.key.toLowerCase() !== pendingKey.toLowerCase());
      tagsToUpload = [...filtered, { key: pendingKey, value: uploadTagValueInput.trim() }];
      // Persist the committed tag back into state so it's visible on the
      // next file's dialog view.
      setUploadTags(tagsToUpload);
      setUploadTagKeyInput("");
      setUploadTagValueInput("");
    }
    setIsUploading(true);
    setUploadError(null);
    try {
      await createClientDocument(clientId, currentUploadFile, tagsToUpload);
      track("document.uploaded");
      const nextIndex = uploadIndex + 1;
      if (nextIndex >= uploadQueue.length) {
        // Batch complete.
        closeUploadDialog();
        await loadDocuments();
      } else {
        // Advance to next file, keep tags in place, refresh list so the
        // user sees each doc appear as it completes.
        setUploadIndex(nextIndex);
        await loadDocuments();
      }
    } catch (err) {
      // Per-file failure: leave uploadIndex where it is so the user sees the
      // failed file still at the front, can retry (click Upload again) or
      // skip past it (Skip button).
      setUploadError(getUserFriendlyErrorMessage(err, { tooLarge: FILE_TOO_LARGE_MESSAGE }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkipCurrentFile = () => {
    if (isUploading) return;
    const nextIndex = uploadIndex + 1;
    setUploadError(null);
    if (nextIndex >= uploadQueue.length) {
      closeUploadDialog();
    } else {
      setUploadIndex(nextIndex);
    }
  };

  // Edit tags handlers
  const openEditTagsDialog = (doc: ClientDocumentDto) => {
    setEditingDoc(doc);
    setEditTags([...doc.tags]);
    setEditTagKeyInput("");
    setEditTagValueInput("");
    setEditTagsDialogOpen(true);
  };

  const handleSaveEditTags = async () => {
    if (!editingDoc) return;
    // Commit any in-progress tag before saving
    let tagsToSave = editTags;
    const pendingKey = editTagKeyInput.trim();
    if (pendingKey) {
      const filtered = tagsToSave.filter((t) => t.key.toLowerCase() !== pendingKey.toLowerCase());
      tagsToSave = [...filtered, { key: pendingKey, value: editTagValueInput.trim() }];
    }
    setIsSavingTags(true);
    try {
      const updated = await updateClientDocumentTags(editingDoc.id, tagsToSave);
      setDocuments((prev) =>
        prev.map((d) => (d.id === updated.id ? updated : d))
      );
      setEditTagsDialogOpen(false);
    } catch (err) {
      console.error("Failed to save tags:", err);
    } finally {
      setIsSavingTags(false);
    }
  };

  // Version history handlers
  const openVersionDialog = async (doc: ClientDocumentDto) => {
    setVersionDoc(doc);
    setVersionDialogOpen(true);
    setIsLoadingVersions(true);
    try {
      const vers = await getClientDocumentVersions(doc.id);
      setVersions(vers);
    } catch (err) {
      console.error("Failed to load versions:", err);
    } finally {
      setIsLoadingVersions(false);
    }
  };

  // Upload new version handlers
  const openUploadVersionDialog = (doc: ClientDocumentDto) => {
    setUploadVersionDoc(doc);
    setUploadVersionFile(null);
    setUploadVersionError(null);
    setUploadVersionDialogOpen(true);
  };

  const acceptUploadVersionFile = (file: File) => {
    if (file.size > 100 * 1024 * 1024) {
      setUploadVersionError("File size must be less than 100MB");
      return;
    }
    setUploadVersionFile(file);
    setUploadVersionError(null);
  };

  const { isDragging: isVersionDragging, handlers: versionDropHandlers } =
    // Version upload takes only the first dropped file — a document has
    // exactly one "next version" slot regardless of how many files land.
    useFileDrop((files) => acceptUploadVersionFile(files[0]), isUploadingVersion);

  const handleVersionFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) acceptUploadVersionFile(file);
  };

  const handleUploadVersionSubmit = async () => {
    if (!uploadVersionDoc || !uploadVersionFile) return;
    setIsUploadingVersion(true);
    setUploadVersionError(null);
    try {
      await uploadClientDocumentVersion(uploadVersionDoc.id, uploadVersionFile);
      setUploadVersionDialogOpen(false);
      await loadDocuments();
    } catch (err) {
      setUploadVersionError(getUserFriendlyErrorMessage(err, { tooLarge: FILE_TOO_LARGE_MESSAGE }));
    } finally {
      setIsUploadingVersion(false);
    }
  };

  // View handler — opens document in Digital Hub viewer.
  // In Excel we use Office.context.ui.displayDialogAsync for the integrated
  // modal experience (dialog sits over the Excel task pane, user stays in
  // context). In standalone that API doesn't exist, but we're already in a
  // browser tab, so a new-window open of the same URL does the same job.
  const handleView = (doc: ClientDocumentDto) => {
    const url = `${environment.urls.digitalHub}/wfi-viewer?documentId=${doc.id}`;
    if (isStandalone) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    Office.context.ui.displayDialogAsync(url, { width: 80, height: 80 }, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Failed to open document viewer dialog:", result.error);
      }
    });
  };

  // Hyperlink handler — writes a HYPERLINK formula to the active cell
  const handleInsertLink = async (doc: ClientDocumentDto) => {
    const url = `${environment.urls.digitalHub}/wfi-viewer?documentId=${doc.id}`;
    const displayName = doc.latestVersion?.fileName || "Document";
    try {
      await Excel.run(async (context) => {
        const cell = context.workbook.getActiveCell();
        cell.formulas = [[`=HYPERLINK("${url}","${displayName.replace(/"/g, '""')}")`]];
        await context.sync();
      });
    } catch (err) {
      console.error("Failed to insert hyperlink:", err);
    }
  };

  // Download handler
  const handleDownload = async (doc: ClientDocumentDto) => {
    if (!doc.latestVersion) return;
    setDownloadingId(doc.id);
    try {
      await downloadClientDocumentVersion(
        doc.id,
        doc.latestVersion.version,
        doc.latestVersion.fileName
      );
    } catch (err) {
      console.error("Failed to download:", err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadVersion = async (ver: ClientDocumentFileDto) => {
    try {
      await downloadClientDocumentVersion(
        ver.clientDocumentId,
        ver.version,
        ver.fileName
      );
    } catch (err) {
      console.error("Failed to download version:", err);
    }
  };

  // Delete handlers
  const handleDeleteClick = (doc: ClientDocumentDto) => {
    setDocToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!docToDelete) return;
    try {
      await deleteClientDocument(docToDelete.id);
      setDocuments((prev) => prev.filter((d) => d.id !== docToDelete.id));
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err));
    } finally {
      setDeleteDialogOpen(false);
      setDocToDelete(null);
    }
  };

  // No client linked
  if (!clientId) {
    return (
      <div className={styles.container}>
        <Title3>Documents</Title3>
        <div className={styles.noClientState}>
          <Document20Regular style={{ fontSize: "48px" }} />
          <Text>No client is linked to this workbook.</Text>
          <Text className={typography.emptyStateBody}>Link a client in Setup to view and manage documents.</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <Title3>Documents</Title3>
          {!isLoading && (
            <Caption1 style={{ marginLeft: tokens.spacingHorizontalS }}>
              {searchQuery
                ? `(${filteredDocuments.length} of ${documents.length})`
                : `(${documents.length})`}
            </Caption1>
          )}
        </div>
        <div className={styles.headerActions}>
          <Button
            appearance="subtle"
            icon={<ArrowSync20Regular />}
            onClick={loadDocuments}
            disabled={isLoading}
            title="Refresh"
          />
          <Button
            appearance="primary"
            icon={<ArrowUpload20Regular />}
            onClick={openUploadDialog}
          >
            Upload
          </Button>
        </div>
      </div>

      {/* Search */}
      <Input
        value={searchQuery}
        onChange={(_e, data) => handleSearchChange(data.value)}
        placeholder="Search documents by file name or tag..."
        contentBefore={<Search20Regular />}
        contentAfter={
          searchQuery ? (
            <Button
              appearance="subtle"
              size="small"
              icon={<Dismiss24Regular />}
              onClick={() => handleSearchChange("")}
              aria-label="Clear search"
            />
          ) : undefined
        }
      />

      {/* Error */}
      {error && (
        <div className={styles.errorContainer}>
          <Text>{error}</Text>
          <Button appearance="secondary" onClick={loadDocuments}>
            Retry
          </Button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className={styles.loadingContainer}>
          <Spinner size="medium" label="Loading documents..." />
        </div>
      )}

      {/* Empty state — no documents at all */}
      {!isLoading && !error && documents.length === 0 && (
        <div className={styles.emptyState}>
          <Document20Regular style={{ fontSize: "48px" }} />
          <Text>No documents found for this client</Text>
          <Text className={typography.emptyStateBody}>Click "Upload" to add a document.</Text>
        </div>
      )}

      {/* Empty state — search matches nothing */}
      {!isLoading && !error && documents.length > 0 && filteredDocuments.length === 0 && (
        <div className={styles.emptyState}>
          <Document20Regular style={{ fontSize: "48px" }} />
          <Text>No documents match "{searchQuery}"</Text>
          <Text className={typography.emptyStateBody}>Try a different file name or tag.</Text>
        </div>
      )}

      {/* Document table */}
      {!isLoading && filteredDocuments.length > 0 && (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th className={styles.th}>File Name</th>
                <th className={styles.th} style={{ width: "80px" }}>Size</th>
                <th className={styles.th} style={{ width: "80px" }}>Version</th>
                <th className={styles.th}>Tags</th>
                <th className={styles.th} style={{ width: "160px" }}>Uploaded</th>
                <th className={styles.th} style={{ width: "130px" }}>Actions</th>
              </tr>
            </thead>
            <tbody className={styles.tbody}>
              {filteredDocuments.map((doc) => (
                <tr key={doc.id} className={styles.tr}>
                  <td className={styles.td}>
                    <div className={styles.nameCell}>
                      <Document20Regular className={styles.nameIcon} />
                      <Text weight="semibold">
                        {doc.latestVersion?.fileName ?? doc.placeholderName ?? "Empty placeholder"}
                      </Text>
                    </div>
                  </td>
                  <td className={styles.td}>
                    <Caption1>
                      {doc.latestVersion ? formatFileSize(doc.latestVersion.fileSize) : "-"}
                    </Caption1>
                  </td>
                  <td className={styles.td}>
                    <span
                      className={styles.versionLink}
                      onClick={() => openVersionDialog(doc)}
                      title="View version history"
                    >
                      {doc.latestVersion ? `v${doc.latestVersion.version}` : "—"}
                    </span>
                  </td>
                  <td className={styles.td}>
                    <div
                      className={styles.tagList}
                      onClick={() => openEditTagsDialog(doc)}
                      title="Edit tags"
                    >
                      {doc.tags.length > 0 ? (
                        doc.tags.map((tag, i) => (
                          <Badge key={i} appearance="outline" size="small" className={styles.tagBadge}>
                            <span style={{ fontWeight: 600 }}>{tag.key}</span>{tag.value ? `: ${tag.value}` : ""}
                          </Badge>
                        ))
                      ) : (
                        <Caption1 style={{ fontStyle: "italic", color: tokens.colorNeutralForeground3 }}>
                          No tags
                        </Caption1>
                      )}
                    </div>
                  </td>
                  <td className={styles.td}>
                    <Caption1>
                      {doc.latestVersion ? formatDate(doc.latestVersion.uploadedAt) : "-"}
                    </Caption1>
                  </td>
                  <td className={styles.td}>
                    <div className={styles.actionsCell}>
                      {/* Both actions require a real file to link to — hidden
                          for placeholders. View works everywhere (Excel gets
                          an in-pane modal via Office dialog; standalone opens
                          a new tab). Insert Hyperlink is Excel-only — it
                          writes a HYPERLINK formula into the active cell. */}
                      {doc.latestVersion && (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<Eye20Regular />}
                          onClick={() => handleView(doc)}
                          title="View in Digital Hub"
                        />
                      )}
                      {doc.latestVersion && !isStandalone && (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<Link20Regular />}
                          onClick={() => handleInsertLink(doc)}
                          title="Insert hyperlink in active cell"
                        />
                      )}
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<ArrowUpload20Regular />}
                        onClick={() => openUploadVersionDialog(doc)}
                        title={doc.latestVersion ? "Upload new version" : "Upload first version"}
                      />
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<ArrowDownload20Regular />}
                        onClick={() => handleDownload(doc)}
                        disabled={!doc.latestVersion || downloadingId === doc.id}
                        title="Download latest"
                      />
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Delete20Regular />}
                        onClick={() => handleDeleteClick(doc)}
                        title="Delete"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onOpenChange={(_e, data) => !data.open && closeUploadDialog()}
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  onClick={closeUploadDialog}
                />
              }
            >
              {uploadQueue.length > 1
                ? `Upload Document (${uploadIndex + 1} of ${uploadQueue.length})`
                : "Upload Document"}
            </DialogTitle>
            <DialogContent>
              <div className={styles.uploadSection}>
                {uploadError && (
                  <div className={styles.errorMessage}>
                    <Text>{uploadError}</Text>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className={styles.hiddenInput}
                  disabled={isUploading}
                  aria-label="Select files to upload"
                />

                {!currentUploadFile ? (
                  <div
                    className={`${styles.filePickerBox} ${isUploadDragging ? styles.filePickerBoxDragging : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
                    }}
                    {...uploadDropHandlers}
                  >
                    <ArrowUpload20Regular style={{ color: tokens.colorNeutralForeground3 }} />
                    <Text style={{ color: tokens.colorNeutralForeground3 }}>
                      {isUploadDragging
                        ? "Drop files to upload"
                        : "Click or drop files here to upload"}
                    </Text>
                  </div>
                ) : (
                  <>
                    <div className={styles.selectedFileInfo}>
                      <Document20Regular />
                      <div style={{ flex: 1 }}>
                        <Text weight="semibold">{currentUploadFile.name}</Text>
                        <br />
                        <Caption1>{formatFileSize(currentUploadFile.size)}</Caption1>
                      </div>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Dismiss24Regular />}
                        disabled={isUploading}
                        onClick={() => removeQueuedFile(uploadIndex)}
                        title="Remove this file from the batch"
                      />
                    </div>
                    {uploadIndex + 1 < uploadQueue.length && (
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                        Next up: {uploadQueue
                          .slice(uploadIndex + 1)
                          .map((f) => f.name)
                          .join(", ")}
                      </Caption1>
                    )}
                  </>
                )}

                <TagInput
                  selectedTags={uploadTags}
                  onTagsChange={setUploadTags}
                  allKnownKeys={allKnownKeys}
                  keyInput={uploadTagKeyInput}
                  onKeyInputChange={setUploadTagKeyInput}
                  valueInput={uploadTagValueInput}
                  onValueInputChange={setUploadTagValueInput}
                  disabled={isUploading}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={closeUploadDialog}>
                Cancel
              </Button>
              {uploadQueue.length > 1 && (
                <Button
                  appearance="secondary"
                  onClick={handleSkipCurrentFile}
                  disabled={isUploading || !currentUploadFile}
                >
                  Skip
                </Button>
              )}
              <Button
                appearance="primary"
                onClick={handleUploadSubmit}
                disabled={!currentUploadFile || isUploading}
              >
                {isUploading
                  ? "Uploading..."
                  : uploadIndex + 1 < uploadQueue.length
                    ? "Upload & Next"
                    : "Upload"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Edit Tags Dialog */}
      <Dialog
        open={editTagsDialogOpen}
        onOpenChange={(_e, data) => !data.open && setEditTagsDialogOpen(false)}
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  onClick={() => setEditTagsDialogOpen(false)}
                />
              }
            >
              Edit Tags
            </DialogTitle>
            <DialogContent>
              {editingDoc && (
                <div className={styles.uploadSection}>
                  <Caption1>{editingDoc.latestVersion?.fileName ?? editingDoc.placeholderName ?? "Empty placeholder"}</Caption1>
                  <TagInput
                    selectedTags={editTags}
                    onTagsChange={setEditTags}
                    allKnownKeys={allKnownKeys}
                    keyInput={editTagKeyInput}
                    onKeyInputChange={setEditTagKeyInput}
                    valueInput={editTagValueInput}
                    onValueInputChange={setEditTagValueInput}
                    disabled={isSavingTags}
                  />
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setEditTagsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSaveEditTags}
                disabled={isSavingTags}
              >
                {isSavingTags ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog
        open={versionDialogOpen}
        onOpenChange={(_e, data) => !data.open && setVersionDialogOpen(false)}
      >
        <DialogSurface className={styles.wideDialogSurface}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  onClick={() => setVersionDialogOpen(false)}
                />
              }
            >
              Version History
            </DialogTitle>
            <DialogContent>
              {versionDoc && (
                <Caption1 style={{ display: "block", marginBottom: tokens.spacingVerticalM }}>
                  {versionDoc.latestVersion?.fileName
                    ?? (versionDoc.placeholderName
                      ? `${versionDoc.placeholderName} — no versions yet`
                      : "Empty placeholder — no versions yet")}
                </Caption1>
              )}
              {isLoadingVersions ? (
                <div style={{ display: "flex", justifyContent: "center", padding: tokens.spacingVerticalL }}>
                  <Spinner size="small" />
                </div>
              ) : (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead className={styles.thead}>
                      <tr>
                        <th className={styles.th} style={{ width: "80px" }}>Version</th>
                        <th className={styles.th}>File Name</th>
                        <th className={styles.th} style={{ width: "80px" }}>Size</th>
                        <th className={styles.th} style={{ width: "160px" }}>Uploaded</th>
                        <th className={styles.th} style={{ width: "60px" }}></th>
                      </tr>
                    </thead>
                    <tbody className={styles.tbody}>
                      {versions.map((ver) => (
                        <tr key={ver.id} className={styles.tr}>
                          <td className={styles.td}>
                            <Text weight="semibold">v{ver.version}</Text>
                            {ver.version === versions[0]?.version && (
                              <Caption1 style={{ color: tokens.colorPaletteGreenForeground1, marginLeft: "4px" }}>
                                (latest)
                              </Caption1>
                            )}
                          </td>
                          <td className={styles.td}>{ver.fileName}</td>
                          <td className={styles.td}>
                            <Caption1>{formatFileSize(ver.fileSize)}</Caption1>
                          </td>
                          <td className={styles.td}>
                            <Caption1>{formatDate(ver.uploadedAt)}</Caption1>
                          </td>
                          <td className={styles.td}>
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<ArrowDownload20Regular />}
                              onClick={() => handleDownloadVersion(ver)}
                              title="Download"
                            />
                          </td>
                        </tr>
                      ))}
                      {versions.length === 0 && (
                        <tr>
                          <td className={styles.td} colSpan={5} style={{ textAlign: "center" }}>
                            <Caption1>No versions found</Caption1>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setVersionDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Upload New Version Dialog */}
      <Dialog
        open={uploadVersionDialogOpen}
        onOpenChange={(_e, data) => !data.open && setUploadVersionDialogOpen(false)}
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  onClick={() => setUploadVersionDialogOpen(false)}
                />
              }
            >
              {uploadVersionDoc?.latestVersion ? "Upload New Version" : "Upload First Version"}
            </DialogTitle>
            <DialogContent>
              <div className={styles.uploadSection}>
                {uploadVersionDoc && (
                  uploadVersionDoc.latestVersion ? (
                    <Caption1>
                      Uploading new version for: <strong>{uploadVersionDoc.latestVersion.fileName}</strong>
                      {" "}(currently v{uploadVersionDoc.latestVersion.version})
                    </Caption1>
                  ) : (
                    <Caption1>
                      Uploading the first file for: <strong>{uploadVersionDoc.placeholderName ?? "this placeholder document"}</strong>
                    </Caption1>
                  )
                )}

                {uploadVersionError && (
                  <div className={styles.errorMessage}>
                    <Text>{uploadVersionError}</Text>
                  </div>
                )}

                <input
                  ref={versionFileInputRef}
                  type="file"
                  onChange={handleVersionFileSelect}
                  className={styles.hiddenInput}
                  disabled={isUploadingVersion}
                  aria-label="Select file for new version"
                />

                {!uploadVersionFile ? (
                  <div
                    className={`${styles.filePickerBox} ${isVersionDragging ? styles.filePickerBoxDragging : ""}`}
                    onClick={() => versionFileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") versionFileInputRef.current?.click();
                    }}
                    {...versionDropHandlers}
                  >
                    <ArrowUpload20Regular style={{ color: tokens.colorNeutralForeground3 }} />
                    <Text style={{ color: tokens.colorNeutralForeground3 }}>
                      {isVersionDragging ? "Drop file to upload" : "Click or drop a file here to upload"}
                    </Text>
                  </div>
                ) : (
                  <div className={styles.selectedFileInfo}>
                    <Document20Regular />
                    <div style={{ flex: 1 }}>
                      <Text weight="semibold">{uploadVersionFile.name}</Text>
                      <br />
                      <Caption1>{formatFileSize(uploadVersionFile.size)}</Caption1>
                    </div>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Dismiss24Regular />}
                      onClick={() => {
                        setUploadVersionFile(null);
                        if (versionFileInputRef.current) versionFileInputRef.current.value = "";
                      }}
                    />
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setUploadVersionDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleUploadVersionSubmit}
                disabled={!uploadVersionFile || isUploadingVersion}
              >
                {isUploadingVersion ? "Uploading..." : "Upload Version"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(_e, data) => !data.open && setDeleteDialogOpen(false)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogContent>
              Are you sure you want to delete "
              {docToDelete?.latestVersion?.fileName ?? docToDelete?.placeholderName ?? "this document"}"? This will
              remove all versions and cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default Documents;
